import styled from 'styled-components';
import Portal from '@/components/Portal';
import {interceptProps} from '@/styled-components';
import {FULL_SCREEN, getHexWithOpacity, palette} from '@/styles';
import {CloseIconComponent} from '../Icons';
import {typographyStyle} from '../Typography';
import {Backdrop, ModalContainer} from './styled-components';

interface ModalProps extends React.HTMLAttributes<HTMLDivElement> {
  fullWidth?: boolean;
  width?: number;
  title?: string;
  scrollDisabled?: boolean;
  bodyOverflow?: string;
  backdropColor?: string;
  zIndex?: number;
  prevModal?(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void;
  onClose?(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void;
  onBackdropClick?(): void;
}

export const Modal = ({
  title,
  width = 640,
  fullWidth,
  scrollDisabled,
  bodyOverflow = 'auto',
  backdropColor,
  zIndex,
  onClose,
  onBackdropClick,
  ...props
}: ModalProps) => {
  return (
    <Portal>
      <>
        <Backdrop
          backdropColor={getHexWithOpacity(palette.black, 0.3)}
          zIndex={zIndex}
        />
        <ModalContainer zIndex={zIndex} onClick={onBackdropClick}>
          <Wrapper width={width} fullWidth={fullWidth} onClick={e => e.stopPropagation()}>
            {!!title?.length && <Header>{title}</Header>}
            <ScrollWrapper
              scrollDisabled={scrollDisabled}
              bodyOverflow={bodyOverflow}
            >
              <Body {...props} />
            </ScrollWrapper>
            {!!onClose && (
              <Close color={palette.textPrimary} onClick={onClose} />
            )}
          </Wrapper>
        </ModalContainer>
      </>
    </Portal>
  );
};

const Wrapper = styled('div').withConfig({
  shouldForwardProp: interceptProps(['width', 'fullWidth']),
})<ModalProps>`
  flex-grow: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: calc(${FULL_SCREEN.vh} - 32px);
  border-radius: 10px;
  background-color: ${palette.white};
  ${({fullWidth, width}) => !fullWidth && `max-width: ${width}px;`}
`;

const Header = styled.header`
  border-bottom: 1px solid ${palette.gray200};
  padding: 14px 24px;
  font-weight: 700;
  flex-shrink: 0;
  ${{...typographyStyle.medium16}}
`;

const ScrollWrapper = styled('div').withConfig({
  shouldForwardProp: interceptProps(['scrollDisabled', 'bodyOverflow']),
})<ModalProps>`
  flex-grow: 1;
  ${({scrollDisabled, bodyOverflow}) =>
    `overflow-y: ${scrollDisabled ? 'hidden' : bodyOverflow};`}
`;

const Body = styled.div``;

const Close = styled(CloseIconComponent)<{
  onClick?: any;
}>`
  position: absolute;
  right: 24px;
  top: 12px;
  width: 24px;
  height: 24px;
  cursor: pointer;
`;

export {Header as ModalHeader};
export type {ModalProps};
